import {createTheme} from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const palette = {
    primary: {
        main: "#272661",
    },
    secondary: {
        main: "#D7273C",
    },
    text: {
        primary: "#000000",
    },
    accent: {
        main: "#FFC727",
        light: "#e7cd84",
    },
};

const theme = createTheme({
    palette,
    typography: {
        fontFamily: "\"Barlow\", \"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: {
            fontSize: 28,
            fontWeight: 700,
            color: palette.primary.main,
        },
        h2: {
            fontSize: 20,
            fontWeight: 700,
            color: palette.primary.main,
        },
        h3: {
            fontSize: 18,
            fontWeight: 700,
            color: palette.primary.main,
        },
        h4: {
            fontSize: 16,
            fontWeight: 700,
            color: palette.primary.main,
        },
        body1: {
            fontSize: 18,
            fontWeight: 400,
            color: "#000000",
        },
        body2: {
            fontSize: 16,
            fontWeight: 400,
            color: "#000000",
            lineHeight: 1.2,
        },
        accent: {
            fontSize: 18,
            fontWeight: 700,
            color: palette.secondary.main,
            textTransform: "uppercase",
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontSize: "18px",
                },
            },
            variants: [
                {
                    props: {variant: "gradient"},
                    style: ({theme}) => ({
                        background: "linear-gradient(90deg, #252968 0%, #3BC8F0 100%)",
                        boxShadow: "0px 10px 18px 0px #00000040",
                        borderTopLeftRadius: 10,
                        borderBottomRightRadius: 10,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: 0,
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: 22,
                        padding: "12px 40px",
                        "&:hover": {
                            background: "linear-gradient(90deg, #1f2154 0%, #2fa8cc 100%)",
                            boxShadow: "0px 12px 20px 0px #00000050",
                            transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                            background: "#D9D9D9",
                            boxShadow: "none",
                            opacity: 0.9,
                        },
                        [theme.breakpoints.down("md")]: {
                            padding: "8px 20px",
                            fontSize: 18,
                        },
                    }),
                },
                {
                    props: {variant: "back"},
                    style: ({theme}) => ({
                        background: "none",
                        border: "none",
                        color: palette.primary.main,
                        fontWeight: 700,
                        fontSize: 22,
                        padding: "8px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: 0,
                        boxShadow: "none",
                        "&:hover": {
                            background: "none",
                            opacity: 0.8,
                            boxShadow: "none",
                        },
                        [theme.breakpoints.down("md")]: {
                            padding: "8px 12px",
                            fontSize: 18,
                        },
                    }),
                },
                {
                    props: {variant: "accent"},
                    style: ({theme}) => ({
                        background: "none",
                        border: "2px solid",
                        borderColor: palette.accent.main,
                        borderRadius: 0,
                        backgroundColor: palette.accent.main,
                        color: palette.primary.main,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        padding: "8px 20px",
                        display: "flex",
                        alignItems: "center",
                        gap: 0,
                        boxShadow: "none",
                        "&:hover": {
                            borderColor: palette.primary.main,
                            boxShadow: "none",
                        },
                        [theme.breakpoints.down("md")]: {
                            padding: "8px 12px",
                            fontSize: 18,
                        },
                    }),
                },
                {
                    props: {variant: "outlined"},
                    style: ({theme}) => ({
                        background: "none",
                        border: "2px solid",
                        borderColor: palette.primary.main,
                        color: palette.primary.main,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        padding: "8px 20px",
                        display: "flex",
                        alignItems: "center",
                        gap: 0,
                        boxShadow: "none",
                        "&:hover": {
                            borderColor: palette.primary.main,
                            backgroundColor: "rgba(39, 38, 97, 0.04)",
                            boxShadow: "none",
                        },
                        [theme.breakpoints.down("md")]: {
                            padding: "8px 16px",
                        },
                    }),
                },
            ],
        },
        MuiInputLabel: {
            defaultProps: {
                shrink: true,
            },
            styleOverrides: {
                root: {
                    fontSize: 18,
                    fontWeight: 400,
                    color: "#000000",
                    position: "relative",
                    transform: "none",
                    marginBottom: 4,
                    "&.Mui-error": {
                        color: "#000000",
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                    backgroundColor: "#F5F5F5",
                    borderRadius: 0,
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #000000",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #000000",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "2px solid #272661",
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        backgroundColor: "rgba(200,125,134,0.1)",
                        border: "none",
                        borderBottom: "1px solid #D7273C",
                    },
                    "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        backgroundColor: "rgba(200,125,134,0.1)",
                        border: "none",
                        borderBottom: "2px solid #D7273C",
                    },
                },
                input: {
                    padding: "12px 16px",
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: 13,
                    marginLeft: 0,
                    marginTop: 4,
                    lineHeight: 1.1,
                    "&.Mui-error": {
                        color: palette.secondary.main,
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: "outlined",
                InputLabelProps: {
                    shrink: true,
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                displayEmpty: true,
                IconComponent: KeyboardArrowDownIcon,
            },
            styleOverrides: {
                root: {
                    fontSize: 18,
                    backgroundColor: "#F5F5F5",
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #000000",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #000000",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "2px solid #272661",
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        backgroundColor: "rgba(200,125,134,0.1)",
                        border: "none",
                        borderBottom: "1px solid #D7273C",
                    },
                    "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        backgroundColor: "rgba(200,125,134,0.1)",
                        border: "none",
                        borderBottom: "2px solid #D7273C",
                    },
                    "& .MuiBox-root": {
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                    },
                },
                select: {
                    padding: "12px 16px",
                    "&.MuiSelect-multiple": {
                        padding: "9px 16px",
                        minHeight: 32,
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    "& .MuiInputLabel-root": {
                        position: "relative",
                        transform: "none",
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: 18,
                    fontWeight: 400,
                    color: "#000000",
                },
                asterisk: {
                    color: palette.secondary.main,
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    padding: 0,
                    position: "relative",
                    "& .MuiSvgIcon-root": {
                        display: "none",
                    },
                    "&::before": {
                        content: "\"\"",
                        width: 24,
                        height: 24,
                        border: "2px solid #272661",
                        borderRadius: "50%",
                        display: "block",
                        backgroundColor: "#FFFFFF",
                        boxSizing: "border-box",
                    },
                    "&.Mui-checked::before": {
                        boxShadow: "inset 0 0 0 1.5px #FFFFFF, inset 0 0 0 24px #272661",
                    },
                    "&.Mui-checked::after": {
                        content: "\"\"",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: 5,
                        height: 10,
                        border: "solid #FFFFFF",
                        borderWidth: "0 1px 1px 0",
                        transform: "translate(-50%, -60%) rotate(45deg)",
                        zIndex: 1,
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 32,
                },
                label: {
                    fontSize: 18,
                    fontWeight: 400,
                    color: "#000000",
                    marginLeft: 10,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: 0,
                    position: "relative",
                    "& .MuiSvgIcon-root": {
                        display: "none",
                    },
                    "&::before": {
                        content: "\"\"",
                        width: 28,
                        height: 28,
                        border: "2px solid #272661",
                        borderRadius: "4px",
                        display: "block",
                        backgroundColor: "#FFFFFF",
                        boxSizing: "border-box",
                    },
                    "&.Mui-checked::before": {
                        backgroundColor: palette.primary.main,
                    },
                    "&.Mui-checked::after": {
                        content: "\"\"",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: 7,
                        height: 14,
                        border: "solid #FFFFFF",
                        borderWidth: "0 3px 3px 0",
                        transform: "translate(-50%, -60%) rotate(45deg)",
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        padding: "4.5px 12px",
                        paddingRight: 40,
                        fontSize: 18,
                        backgroundColor: "#F5F5F5",
                        borderRadius: 0,
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            borderBottom: "1px solid #000000",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            borderBottom: "1px solid #000000",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            borderBottom: "2px solid #272661",
                        },
                        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            backgroundColor: "rgba(200,125,134,0.1)",
                            border: "none",
                            borderBottom: "1px solid #D7273C",
                        },
                        "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            backgroundColor: "rgba(200,125,134,0.1)",
                            border: "none",
                            borderBottom: "2px solid #D7273C",
                        },
                    },
                    "& .MuiAutocomplete-input": {
                        padding: "12px 16px",
                    },
                },
                listbox: {
                    fontSize: 18,
                },
                option: {
                    fontSize: 18,
                },
                popupIndicator: {
                    color: palette.primary.main,
                },
                clearIndicator: {
                    color: palette.primary.main,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: "#F5F5F5",
                    boxShadow: "0px 2px 8px 0px #00000014",
                    border: "1px solid #E5E7EB",
                    marginBottom: "16px",
                    borderRadius: "0 !important",
                    "&:before": {
                        display: "none",
                    },
                    "&.Mui-expanded": {
                        margin: "0 0 16px 0",
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: "12px 16px",
                    minHeight: "60px",
                    backgroundColor: "#EFF2F5",
                    flexDirection: "row-reverse",
                    gap: 8,
                    "&.Mui-expanded": {
                        minHeight: "60px",
                    },
                },
                content: {
                    margin: "0",
                    "&.Mui-expanded": {
                        margin: "0",
                    },
                },
                expandIconWrapper: {
                    color: palette.primary.main,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: "20px",
                    backgroundColor: "#FFFFFF",
                },
            },
        },
        MuiPickersTextField: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                    borderRadius: 0,
                    "& .MuiPickersOutlinedInput-root": {
                        borderRadius: 0,
                        backgroundColor: "#F5F5F5",
                    },
                    "& .MuiPickersOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #000000",
                    },
                    "&:hover .MuiPickersOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #000000",
                    },
                    "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "2px solid #272661",
                    },
                    "& .Mui-error.MuiPickersOutlinedInput-root": {
                        backgroundColor: "rgba(200,125,134,0.1)",
                    },
                    "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
                        border: "none",
                        borderBottom: "1px solid #D7273C",
                    },
                    "&.Mui-error.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
                        backgroundColor: "rgba(200,125,134,0.1) !important",
                        border: "none",
                        borderBottom: "2px solid #D7273C",
                    },
                    "& .MuiPickersSectionList-root": {
                        padding: "12px 0",
                    },
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    fontSize: 16,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 8,
                    padding: 16,
                    maxWidth: 870,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: 24,
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    // backgroundColor: "#383838D6",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                label: {
                    fontSize: 16,
                },
            },
        },
    },
});

export default theme;
